import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icons from './Icons';

// Theme Toggle Component
export const ThemeToggle = ({ isDark, toggleTheme }) => (
  <button onClick={toggleTheme} className={`p-2 rounded-full transition-all duration-300 ${isDark ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}>
    {isDark ? <Icons.Sun /> : <Icons.Moon />}
  </button>
);

// Top Bar Component
export const TopBar = ({ isDark }) => (
  <div className={`relative overflow-hidden transition-colors duration-300 ${isDark ? 'bg-gradient-to-r from-[#0a0a0a] via-[#1a1a1a] to-[#0a0a0a]' : 'bg-white'}`}>
    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent"></div>
    <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-3">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4 sm:gap-8">
          <a href="tel:9602360369" className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm transition-all duration-300 hover:scale-105 ${isDark ? 'text-gray-400 hover:text-[#c9a962]' : 'text-gray-600 hover:text-[#c9a962]'}`}>
            <div className="relative">
              <div className="absolute inset-0 bg-[#c9a962] rounded-full opacity-20 animate-pulse"></div>
              <div className={`relative p-1 sm:p-1.5 rounded-full ${isDark ? 'bg-gray-800' : 'bg-white shadow'}`}>
                <Icons.Phone />
              </div>
            </div>
            <span className="hidden sm:inline">96023 60369</span>
          </a>
          <a href="mailto:brotherscarrental8286@gmail.com" className={`hidden md:flex items-center gap-2 text-sm transition-all duration-300 hover:scale-105 ${isDark ? 'text-gray-400 hover:text-[#c9a962]' : 'text-gray-600 hover:text-[#c9a962]'}`}>
            <div className={`p-1.5 rounded-full ${isDark ? 'bg-gray-800' : 'bg-white shadow'}`}>
              <Icons.Mail />
            </div>
            <span className="hidden lg:inline">brotherscarrental8286@gmail.com</span>
          </a>
        </div>
        <div className="flex items-center gap-4 sm:gap-6">
          <a href="https://maps.app.goo.gl/4yzmR1M9BsETXkK4A" target="_blank" rel="noopener noreferrer" className={`flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm transition-colors hover:text-[#c9a962] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
            <Icons.MapMarker />
            <span className="hidden sm:inline">Udaipur, Rajasthan</span>
          </a>
          <div className="flex items-center gap-2 sm:gap-3">
            <a href="https://www.facebook.com/share/1NDAhX4RoE/" target="_blank" rel="noopener noreferrer" className={`p-1 sm:p-1.5 rounded-full transition-all duration-300 hover:scale-110 hover:bg-[#c9a962] ${isDark ? 'bg-gray-800 text-gray-400 hover:text-black' : 'bg-white text-gray-600 hover:text-black shadow'}`}>
              <Icons.Facebook />
            </a>
            <a href="https://www.instagram.com/brothers_rental_udaipur/" target="_blank" rel="noopener noreferrer" className={`p-1 sm:p-1.5 rounded-full transition-all duration-300 hover:scale-110 hover:bg-[#E1306C] ${isDark ? 'bg-gray-800 text-gray-400 hover:text-white' : 'bg-white text-gray-600 hover:text-white shadow'}`}>
              <Icons.Instagram />
            </a>
          </div>
        </div>
      </div>
    </div>
    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#c9a962] to-transparent animate-pulse"></div>
  </div>
);

// Navbar Component
const Navbar = ({ isDark, toggleTheme }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Luxury Cars', path: '/luxury-cars' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <nav className={`relative z-50 transition-all duration-500 ${isScrolled ? (isDark ? 'bg-[#0a0a0a]/95 backdrop-blur-md shadow-lg py-2 sm:py-3' : 'bg-white/95 backdrop-blur-md shadow-lg py-2 sm:py-3') : (isDark ? 'bg-[#0a0a0a] py-3 sm:py-5' : 'bg-white py-3 sm:py-5')} ${isDark ? 'dark' : ''}`}>
      {isScrolled && <div className="absolute inset-0 bg-gradient-to-b from-[#c9a962]/10 to-transparent pointer-events-none"></div>}
      <div className="relative max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <Link to="/" className="relative group">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="relative">
                <div className="absolute inset-0 bg-[#c9a962] rounded-lg blur opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
                <img src="/logo.jpg" alt="Brothers Car Rental" className="relative w-10 h-10 sm:w-12 md:w-14 sm:h-12 md:h-14 object-cover rounded-lg shadow-lg" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm sm:text-xl md:text-2xl lg:text-3xl font-bold font-['Playfair_Display'] tracking-wider">
                  <span className="text-[#c9a962]">Brothers</span>
                  <span className={isDark ? 'text-white' : 'text-black'}>Rental</span>
                </span>
                <span className="text-[8px] sm:text-[10px] md:text-xs tracking-[0.2em] sm:tracking-[0.3em] text-gray-700">
                  PREMIUM CAR RENTAL
                </span>
              </div>
            </div>
          </Link>
          <div className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              <Link key={item.name} to={item.path} className="relative group">
                <span className={`font-medium transition-all duration-300 hover:scale-105 ${isDark ? 'text-white hover:text-[#c9a962]' : 'text-black hover:text-[#c9a962]'}`}>
                  {item.name}
                </span>
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#c9a962] transition-all duration-300 group-hover:w-full ${isScrolled ? 'opacity-100' : 'opacity-0'}`}></span>
              </Link>
            ))}
          </div>
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:9602360369" className="relative group overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-[#c9a962] to-[#a08040] opacity-100 group-hover:opacity-0 transition-opacity duration-300"></div>
              <span className="relative z-10 flex items-center gap-2 px-6 py-2.5 bg-[#c9a962] text-black font-semibold uppercase tracking-wider transition-all duration-300 group-hover:text-black">
                <Icons.Key />
                Book Now
              </span>
            </a>
            <ThemeToggle isDark={isDark} toggleTheme={toggleTheme} />
          </div>
          <div className="lg:hidden flex items-center gap-2 sm:gap-4">
            <ThemeToggle isDark={isDark} toggleTheme={toggleTheme} />
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className={`p-2 rounded-lg transition-all duration-300 ${isDark ? 'bg-gray-800 text-white' : 'bg-white text-black shadow'}`}>
              {isMenuOpen ? <Icons.X /> : <Icons.Menu />}
            </button>
          </div>
        </div>
      </div>
      {isMenuOpen && (
        <div className={`lg:hidden border-t ${isDark ? 'bg-[#0a0a0a] border-[#c9a962]/20' : 'bg-white border-gray-200'}`}>
          <div className="px-3 sm:px-4 py-4 sm:py-6 space-y-2 sm:space-y-4">
            {navItems.map((item) => (
              <Link key={item.name} to={item.path} className={`block py-2.5 sm:py-3 px-3 sm:px-4 rounded-lg transition-all duration-300 text-sm sm:text-base ${isDark ? 'text-white hover:bg-gray-800 hover:text-[#c9a962]' : 'text-black hover:bg-gray-100 hover:text-[#c9a962]'}`} onClick={() => setIsMenuOpen(false)}>
                {item.name}
              </Link>
            ))}
            <a href="tel:9602360369" className="flex items-center justify-center gap-2 py-3 sm:py-4 mt-4 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black font-semibold uppercase tracking-wider rounded-lg text-sm sm:text-base">
              <Icons.Key /> Book Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
